import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import { AuthContext } from './AuthContext'

// Create the context
export const WorkspaceContext = createContext()

// Create the provider component
export const WorkspaceProvider = ({ children }) => {
  const { user } = useContext(AuthContext)
  const customFetch = useCustomFetch()
  
  // Workspace state
  const [workspace, setWorkspace] = useState(null)
  const [isPartner, setIsPartner] = useState(false)
  const [isSlackConnected, setIsSlackConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  
  // Add cache and timestamp to prevent redundant requests
  const workspaceCache = useRef({});
  const lastFetchTime = useRef({});
  const fetchInProgress = useRef(false);
  
  // Minimum time between requests (5 seconds)
  const CACHE_DURATION = 5000;

  // Fetch workspace data when the active workspace changes
  useEffect(() => {
    const fetchWorkspaceData = async () => {
      // Skip if no active workspace
      if (!user?.active_workspace) {
        setWorkspace(null);
        setIsPartner(false);
        setIsSlackConnected(false);
        setIsLoading(false);
        return;
      }
      
      const workspaceId = user.active_workspace;
      
      // Check if we already have recent data for this workspace
      const currentTime = Date.now();
      const cachedData = workspaceCache.current[workspaceId];
      const lastFetch = lastFetchTime.current[workspaceId] || 0;
      
      // Use cached data if it exists and is recent
      if (cachedData && (currentTime - lastFetch < CACHE_DURATION)) {
        setWorkspace(cachedData);
        setIsPartner(cachedData?.is_partner === true);
        setIsSlackConnected(cachedData?.integrations?.slack?.connected === true);
        setIsLoading(false);
        return;
      }
      
      // Prevent concurrent requests for the same workspace
      if (fetchInProgress.current) {
        return;
      }
      
      try {
        fetchInProgress.current = true;
        setIsLoading(true);
        
        const response = await customFetch(`/getWorkspaceById/${workspaceId}`, 'GET', null);
        
        if (response.error) {
          console.error('Error fetching workspace data:', response.error);
          setIsLoading(false);
          fetchInProgress.current = false;
          return;
        }

        // Update cache
        workspaceCache.current[workspaceId] = response.workspace;
        lastFetchTime.current[workspaceId] = Date.now();
        
        // Set workspace data
        setWorkspace(response.workspace);
        
        // Set partner status based on the is_partner flag
        setIsPartner(response.workspace?.is_partner === true);
        
        // Check if Slack is connected
        setIsSlackConnected(response.workspace?.integrations?.slack?.connected === true);
      } catch (error) {
        console.error('Error fetching workspace data:', error);
      } finally {
        setIsLoading(false);
        fetchInProgress.current = false;
      }
    };

    fetchWorkspaceData();
    
    // Clean up the fetch in progress flag if component unmounts during fetch
    return () => {
      fetchInProgress.current = false;
    };
  }, [user?.active_workspace]);

  // Manually refresh workspace data with forced update
  const refreshWorkspace = async (force = false) => {
    if (!user?.active_workspace) {
      return;
    }
    
    const workspaceId = user.active_workspace;
    
    // Skip if a fetch is already in progress
    if (fetchInProgress.current) {
      return;
    }
    
    // Check cache unless forced refresh
    const currentTime = Date.now();
    const cachedData = workspaceCache.current[workspaceId];
    const lastFetch = lastFetchTime.current[workspaceId] || 0;
    
    if (!force && cachedData && (currentTime - lastFetch < CACHE_DURATION)) {
      return;
    }
    
    try {
      fetchInProgress.current = true;
      setIsLoading(true);
      
      const response = await customFetch(`/getWorkspaceById/${workspaceId}`, 'GET', null);
      
      if (!response.error) {
        // Update cache
        workspaceCache.current[workspaceId] = response.workspace;
        lastFetchTime.current[workspaceId] = Date.now();
        
        // Update state
        setWorkspace(response.workspace);
        setIsPartner(response.workspace?.is_partner === true);
        setIsSlackConnected(response.workspace?.integrations?.slack?.connected === true);
      }
    } catch (error) {
      console.error('Error refreshing workspace data:', error);
    } finally {
      setIsLoading(false);
      fetchInProgress.current = false;
    }
  };

  return (
    <WorkspaceContext.Provider 
      value={{ 
        workspace, 
        isPartner,
        isSlackConnected,
        isLoading,
        refreshWorkspace
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  )
}

WorkspaceProvider.propTypes = {
  children: PropTypes.node,
}

// Custom hook to use the workspace context
export const useWorkspace = () => {
  const context = useContext(WorkspaceContext)
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider')
  }
  return context
} 